import { isDefined } from '@evorait/pace-core';
import { CHECKPOINTS_STATUS } from '../types/checkpoints-status';
import { FormParticipantCopies } from '../types/form-participant-copies';
import { FormsConstants } from '../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE } from '../types/functional-element';
import { ParticipantCopy, ParticipantCopyId } from '../types/participant-copy';
import { RADIO_OPTION_TYPE } from '../types/radio-option';
import { Signature } from '../types/signature';
import { DEFICIENCY_SELECTION, MEASUREMENT_SELECTION, SystemCondition } from '../types/system-condition';
import { FormUtilities } from './form-utilities';
import SUBMIT_STATUS = FormsConstants.SUBMIT_STATUS;

export class SubmitStatusUtilities {
    public static finalOrInterimReducer(
        accu: SUBMIT_STATUS,
        status: CHECKPOINTS_STATUS,
    ):
        | SUBMIT_STATUS.HANDOVER
        | SUBMIT_STATUS.FINAL
        | SUBMIT_STATUS.INTERIM
        | SUBMIT_STATUS.INTERIM_NOT_OK
        | SUBMIT_STATUS.FINAL_NOT_OK {
        // Shortcut: If accu is undefined, we already know that we cannot submit and the the status of the current section
        // does not matter anymore, so we return undefined as well
        if (accu === SUBMIT_STATUS.HANDOVER) {
            return SUBMIT_STATUS.HANDOVER;
        } else if (accu === SUBMIT_STATUS.INTERIM_NOT_OK) {
            // if previous segments are filled with INTERIM_NOT_OK, no other status can be reached again
            return SUBMIT_STATUS.INTERIM_NOT_OK;
        }

        // If the form cannot be submitted yet, we return undefined, otherwise the respective save-mode.
        let newSubmitMode:
            | SUBMIT_STATUS.HANDOVER
            | SUBMIT_STATUS.INTERIM
            | SUBMIT_STATUS.FINAL
            | SUBMIT_STATUS.INTERIM_NOT_OK
            | SUBMIT_STATUS.FINAL_NOT_OK = SUBMIT_STATUS.HANDOVER;

        if (
            // Empty and partially filled sections are ok for interim
            [CHECKPOINTS_STATUS.EMPTY, CHECKPOINTS_STATUS.PARTIALLY_FILLED].includes(status)
        ) {
            newSubmitMode = SUBMIT_STATUS.INTERIM;
        } else if (status === CHECKPOINTS_STATUS.PARTIALLY_FILLED_NOT_OK) {
            newSubmitMode = SUBMIT_STATUS.INTERIM_NOT_OK;
        } else if (
            // all other fill states result in a final
            [CHECKPOINTS_STATUS.NONE, CHECKPOINTS_STATUS.COMPLETELY_FILLED].includes(status)
        ) {
            newSubmitMode = SUBMIT_STATUS.FINAL;
        } else if (status === CHECKPOINTS_STATUS.COMPLETELY_FILLED_NOT_OK) {
            newSubmitMode = SUBMIT_STATUS.FINAL_NOT_OK;
        }

        // we are taking previous sections into account.
        // Depending on the current and the previous section we might have
        // to change to a "not ok" version of the status. Only INTERIM_NOT_OK is never
        // checked as this is the last state and there cannot be any transition
        // from it (is checked at the start of this method)
        if (newSubmitMode === SUBMIT_STATUS.FINAL) {
            if (accu === SUBMIT_STATUS.INTERIM) {
                // previous sections are not completely filled, so still INTERIM
                newSubmitMode = SUBMIT_STATUS.INTERIM;
            } else if (accu === SUBMIT_STATUS.FINAL_NOT_OK) {
                // everything is filled so far, but also with "not ok" sections
                newSubmitMode = SUBMIT_STATUS.FINAL_NOT_OK;
            }
        } else if (newSubmitMode === SUBMIT_STATUS.FINAL_NOT_OK) {
            if (accu === SUBMIT_STATUS.INTERIM) {
                // previous sections are not completely filled and the current one contains "not ok"
                newSubmitMode = SUBMIT_STATUS.INTERIM_NOT_OK;
            }
        } else if (newSubmitMode === SUBMIT_STATUS.INTERIM) {
            if (accu === SUBMIT_STATUS.FINAL_NOT_OK) {
                // everything is filled so far, with some sections as "not ok", so it becomes an INTERIM_NOT_OK now
                newSubmitMode = SUBMIT_STATUS.INTERIM_NOT_OK;
            }
        }

        return newSubmitMode;
    }

    // submitting is only possible if the copy was not submitted before
    public static hasBeenSubmitted(participantCopy: ParticipantCopy): boolean {
        return participantCopy.submittedOn.length > 0;
    }

    // ensure all participants have copies created
    // check each participant copy for signatures
    public static hasSignedParticipantCopies(
        selectedParticipant: ParticipantCopyId,
        participantCopies: FormParticipantCopies,
    ): boolean {
        const participantsWithCopies = Object.values(participantCopies)
            .filter((participantCopy) => participantCopy.id !== FormsConstants.MASTER_PARTICIPANT_ID)
            .filter((participantCopy) => participantCopy.hasCopy);

        // without participant copies the trainer did not conduct a valid training
        const hasAtLeastOneParticipantWithCopy = participantsWithCopies.length >= 1;

        // if there are participants without copy, then the trainer needs to either create a copy for this participant
        // or delete the participant. But there are no participants without copies allowed when the master is submitted.
        const everyParticipantHasValidCopy =
            participantsWithCopies.length === Object.values(participantCopies).length - 1;

        // ensure that every participant copy has all required signatures. Unsigned participant copies cannot be
        // submitted, so we disallow the submit of the master as well.
        const everyParticipantCopyHasSignatures = !participantsWithCopies.some(
            (participantCopy) => !SubmitStatusUtilities.isSignedParticipantCopy(participantCopy),
        );

        return (
            // ensure there is at least one participant copy
            hasAtLeastOneParticipantWithCopy &&
            // ensure that every participant has a valid copy
            everyParticipantHasValidCopy &&
            // ensure there are no participant copies without signature
            everyParticipantCopyHasSignatures
        );
    }

    // allow submission of participant copy only if the participant copy has the signatures filled
    public static isSignedParticipantCopy(participantCopy: ParticipantCopy): boolean {
        return Object.values(participantCopy.functionalElements)
            .filter(
                (f) =>
                    f.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER ||
                    f.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE ||
                    f.type === FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE ||
                    f.type === FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE,
            )
            .map((signature: Signature) => {
                // Astoria Signatures
                if (
                    signature.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER ||
                    signature.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE
                ) {
                    // get the selected option of the controlling checkpoint for the signature
                    const options = participantCopy.options[signature.controllingCheckpoint];
                    if (typeof options === 'undefined') {
                        throw new Error(
                            `SubmitStatusUtilities: isSignedParticipantCopy: no options found for controlling checkpoint '${signature.controllingCheckpoint}'`,
                        );
                    }
                    const selectedOptionOfControllingCheckpoint = options.find((checkpoint) => checkpoint.selected);

                    return (
                        // We enforce that a checkpoint selection was done.
                        typeof selectedOptionOfControllingCheckpoint !== 'undefined' &&
                        // if the controlling checkpoint of a signature is marked as N.A. the signature gets cleared.
                        // If N.A. is selected, a submission of the form should be possible.
                        (selectedOptionOfControllingCheckpoint.optionType === RADIO_OPTION_TYPE.NA ||
                            (signature.signature.length > 0 &&
                                signature.firstName.length > 0 &&
                                signature.lastName.length > 0 &&
                                (!signature.emailRequired || signature.email.length > 0)))
                    );
                } else if (
                    signature.type === FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE ||
                    signature.type === FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE
                ) {
                    return FormUtilities.isCosimaSignatureValid(signature);
                } else {
                    throw new Error(
                        `SubmitStatusUtilities: isSignedParticipantCopy: unhandled signature type identified '${signature.type}'`,
                    );

                    return false;
                }
            })
            .reduce((accu, signatureComplete) => accu && signatureComplete, true);
    }

    public static systemConditionValid(systemCondition: SystemCondition): boolean {
        const choices = systemCondition.choices;

        const deficiencySelection = isDefined(choices)
            ? Object.values(choices)
                  .filter(isDefined)
                  .some((c) => c.selected)
            : systemCondition.deficienciesSelection !== DEFICIENCY_SELECTION.NO_SELECTION;

        const isMeasurementSelectionInvalid =
            isDefined(systemCondition.measurementSelection) &&
            systemCondition.measurementSelection === MEASUREMENT_SELECTION.NO_SELECTION;

        return (
            deficiencySelection &&
            !isMeasurementSelectionInvalid &&
            systemCondition.employeeSignature.firstName.length > 0 &&
            systemCondition.employeeSignature.lastName.length > 0 &&
            systemCondition.employeeSignature.signature.length > 0 &&
            (typeof systemCondition.customerSignature === 'undefined' ||
                (systemCondition.customerSignature.firstName.length > 0 &&
                    systemCondition.customerSignature.lastName.length > 0 &&
                    (systemCondition.customerSignature.signature.length > 0 ||
                        (systemCondition.customerSignature.firstName === FormsConstants.NA &&
                            systemCondition.customerSignature.lastName === FormsConstants.NA))))
        );
    }
}
